import type { StaticImageData } from "next/image";
import Image from "next/image";
import back from "public/images/SpellbookBack.png";
import { useMemo } from "react";
import { BsEyeSlashFill } from "react-icons/bs";
import { GiCometSpark, GiCrossedSwords } from "react-icons/gi";

import { CardCategoryEnum } from "@sorcery/db";
import { InternalLink, Line } from "@sorcery/ui";
import { fromNow, isNew } from "@sorcery/utils/universal/time";

import type { DeckPreviewType } from "~/types/deck";
import { hasPrimer, isPrivate } from "~/utils/deck";
import { deckPath, userPath } from "~/utils/paths";
import { Metadata } from "../Metadata";
import { FannedDeckCardPreview } from "../card/FannedDeckCardPreview";
import { DeckThreshholds } from "./DeckThreshholds";

type Props = {
  deck: DeckPreviewType;
};

export const DeckPreview = ({ deck }: Props) => {
  const preview = useMemo(() => {
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    const p: (string | StaticImageData)[] = [deck.avatars[0]!.variant.src].concat(
      deck.decklist.slice(0, 2).map((card) => card.variant.src),
    );
    while (p.length < 3) {
      p.push(back);
    }
    return p;
  }, [deck.decklist, deck.avatars]);

  const rotate = deck.feature.card.guardian?.category === CardCategoryEnum.Site;
  const transform = useMemo(() => {
    return rotate
      ? "scale-[0.9] rotate-[90deg] translate-y-[-20%] group-hover:scale-[1]"
      : "translate-y-[-12%] group-hover:scale-[1.07]";
  }, [rotate]);

  return (
    <div className="duration-700 animate-in fade-in">
      <InternalLink
        href={deckPath(deck.id)}
        className="group mx-auto flex flex-col rounded-xl border border-transparent transition-all hover:border-white
          hover:shadow-[0_0_14px_rgba(255,255,255,0.75)]"
      >
        <div
          className="relative h-[210px] w-full rounded-xl"
          style={{
            backgroundImage:
              "linear-gradient(to top, rgb(2, 2, 2, 0.9) 0%, rgba(2, 2, 2, 0.8) 30%, rgba(2, 2, 2, 0.3) 50%, transparent 80%)",
          }}
        >
          {/* Backdrop */}
          <div className="absolute left-0 top-0 h-full w-full overflow-hidden rounded-xl">
            <div className="absolute right-0 top-0 h-full w-[60%] gradient-mask-l-50 xl:w-[65%]">
              <div className="absolute right-0 top-0 h-full w-full gradient-mask-b-40">
                <div className="absolute right-0 top-0 h-full w-full gradient-mask-b-40">
                  <Image
                    height={600}
                    width={420}
                    className={`rounded-xl brightness-[0.9] transition-all duration-300 gradient-mask-b-40 ${transform}`}
                    alt={deck.name}
                    src={deck.feature.src}
                  />
                </div>
              </div>
            </div>
            {/* Card fan */}
            <div className="absolute left-0 top-3 h-[45%] w-3/4 gradient-mask-b-70 sm:w-1/2">
              <div className="absolute h-full w-full pl-5">
                {/* Hand fan */}
                <div className="absolute mx-auto flex h-full w-full max-w-[180px] brightness-110 sm:max-w-[110px] md:max-w-[120px] 2xl:max-w-[135px] 3xl:max-w-[160px]">
                  {preview.map((src, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                    <FannedDeckCardPreview key={i} src={src} position={i} />
                  ))}
                </div>
              </div>
            </div>
            {/* <div
              className="absolute h-full w-full"
              style={{
                backgroundImage: `
          linear-gradient(240deg, rgba(2, 2, 2, 0.8) 0%, rgba(2, 2, 2, 0.1) 20%),
          linear-gradient(120deg, rgba(2, 2, 2, 0.8) 0%, rgba(2, 2, 2, 0.1) 20%),
          linear-gradient(180deg, rgba(2, 2, 2, 0.3) 0%, rgba(2, 2, 2, 0.1) 30%)`,
              }}
            ></div> */}
          </div>

          {/* Card */}
          <div className="h-full p-4 !pb-3">
            <div className="flex h-full flex-col justify-end">
              <div className="flex justify-between text-sm">
                {/* badges */}
                <div className="flex">
                  {isNew(deck.createdAt) && (
                    <div className="whitespace-nowrap pr-2">
                      <GiCometSpark size={11} className="-mt-1 mr-1 inline text-white" />

                      <span className="text-white">New</span>
                    </div>
                  )}

                  {hasPrimer(deck.primer) && (
                    <div className="whitespace-nowrap pr-2">
                      <GiCrossedSwords size={11} className="text-red -mt-0.5 mr-0.5 inline" />

                      <span className="text-red">Primer</span>
                    </div>
                  )}

                  {isPrivate(deck.visibility) && (
                    <div className="whitespace-nowrap pr-2">
                      <BsEyeSlashFill size={11} className="text-info -mt-[3px] mr-0.5 inline" />

                      <span className="text-info">Private</span>
                    </div>
                  )}
                </div>
                {/* affinity */}
                <DeckThreshholds elements={deck.elements} />
              </div>
              <Line className="my-2" />

              <div className="flex flex-row items-center justify-between">
                <div className="mb-0 mr-2 truncate text-xl font-bold text-big-text-color">
                  {deck.name}
                </div>
              </div>

              <div className="flex justify-between text-sm">
                <div className="truncate pr-2 text-sm opacity-80">{fromNow(deck.updatedAt)}</div>
                <Metadata
                  views={deck._count.views}
                  comments={deck._count.comments}
                  likes={deck._count.likes}
                  className="opacity-80"
                />
              </div>
              <div className="flex justify-between text-sm">
                <div className="pr-2 opacity-80">{deck.format}</div>
                <InternalLink
                  href={userPath(deck.user.id)}
                  className="hover:text-primary opacity-80 hover:underline"
                >
                  <div className="truncate">@{deck.user.username}</div>
                </InternalLink>
              </div>
            </div>
          </div>
        </div>
      </InternalLink>
    </div>
  );
};
